import { useState, useEffect } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Activity, Zap, TrendingDown, Users, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

interface UsageRow {
  module?: string;
  model?: string;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
}

interface UsageResp {
  days: number;
  total_calls: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost_usd: number;
  total_cost_eur: number;
  by_module_model: UsageRow[];
}

interface AdminResp {
  days: number;
  global: { calls: number; input_tokens: number; output_tokens: number; cost_usd: number; cost_eur: number };
  by_model: { provider: string; model: string; calls: number; cost_usd: number }[];
  top_users: { user_id: string; calls: number; cost_usd: number }[];
}

const fmtCost = (v: number) => `${v.toFixed(4)} $`;
const fmtCostEur = (v: number) => `${(v * 0.92).toFixed(3)} €`;
const fmtTokens = (v: number) => v.toLocaleString("fr-FR");

export default function LlmUsageDashboard() {
  const [mode, setMode] = useState<"me" | "admin">("me");
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [me, setMe] = useState<UsageResp | null>(null);
  const [admin, setAdmin] = useState<AdminResp | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/auth/me`, { credentials: "include" as RequestCredentials })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => setIsAdmin(u?.role === "admin"))
      .catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      if (mode === "me") {
        const r = await fetch(`${API_URL}/api/user/usage?days=${days}`, {
          credentials: "include" as RequestCredentials,
        });
        if (r.ok) setMe(await r.json());
      } else {
        const r = await fetch(`${API_URL}/api/admin/usage/summary?days=${days}`, {
          credentials: "include" as RequestCredentials,
        });
        if (r.ok) setAdmin(await r.json());
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
     
  }, [mode, days]);

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 overflow-y-auto">
          <div className="relative border-b border-border bg-gradient-to-br from-primary/5 via-background to-accent/10 px-6 py-8 md:px-10 md:py-10">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Activity className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h1 data-testid="usage-title" className="text-2xl md:text-3xl font-bold tracking-tight">
                    Consommation LLM
                  </h1>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Tokens consommés, coûts estimés et répartition par modèle.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <div className="inline-flex rounded-lg border border-border/50 p-1 bg-card">
                    <Button
                      size="sm"
                      variant={mode === "me" ? "secondary" : "ghost"}
                      onClick={() => setMode("me")}
                      data-testid="usage-tab-me"
                    >
                      Mon compte
                    </Button>
                    <Button
                      size="sm"
                      variant={mode === "admin" ? "secondary" : "ghost"}
                      onClick={() => setMode("admin")}
                      data-testid="usage-tab-admin"
                    >
                      Admin (global)
                    </Button>
                  </div>
                )}
                <select
                  value={days}
                  onChange={(e) => setDays(Number(e.target.value))}
                  className="text-xs px-3 py-1.5 rounded-lg border border-border/50 bg-card"
                  data-testid="usage-days-select"
                >
                  <option value={7}>7 derniers jours</option>
                  <option value={30}>30 derniers jours</option>
                  <option value={90}>90 derniers jours</option>
                </select>
              </div>
            </div>
          </div>

          <div className="px-6 py-8 md:px-10 space-y-6">
            {loading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Chargement...
              </div>
            )}

            {!loading && mode === "me" && me && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="usage-kpis-me">
                  <KPI icon={Zap} label="Appels LLM" value={String(me.total_calls)} />
                  <KPI icon={Activity} label="Tokens entrée" value={fmtTokens(me.total_input_tokens)} />
                  <KPI icon={Activity} label="Tokens sortie" value={fmtTokens(me.total_output_tokens)} />
                  <KPI icon={TrendingDown} label="Coût estimé" value={`${me.total_cost_eur.toFixed(3)} €`} />
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Détail par module & modèle</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {me.by_module_model.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Aucun appel sur la période.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="usage-table-me">
                          <thead>
                            <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider border-b border-border/50">
                              <th className="py-2 pr-4">Module</th>
                              <th className="py-2 pr-4">Modèle</th>
                              <th className="py-2 pr-4 text-right">Appels</th>
                              <th className="py-2 pr-4 text-right">Tokens entrée</th>
                              <th className="py-2 pr-4 text-right">Tokens sortie</th>
                              <th className="py-2 pr-4 text-right">Coût</th>
                            </tr>
                          </thead>
                          <tbody>
                            {me.by_module_model.map((r, i) => (
                              <tr key={i} className="border-b border-border/30 hover:bg-card/40">
                                <td className="py-2 pr-4">
                                  <Badge variant="outline" className="text-xs">{r.module || "-"}</Badge>
                                </td>
                                <td className="py-2 pr-4 font-mono text-xs">{r.model || "-"}</td>
                                <td className="py-2 pr-4 text-right">{r.calls}</td>
                                <td className="py-2 pr-4 text-right">{fmtTokens(r.input_tokens)}</td>
                                <td className="py-2 pr-4 text-right">{fmtTokens(r.output_tokens)}</td>
                                <td className="py-2 pr-4 text-right font-semibold text-primary">{fmtCost(r.cost_usd)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            {!loading && mode === "admin" && admin && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="usage-kpis-admin">
                  <KPI icon={Zap} label="Appels (global)" value={String(admin.global.calls)} />
                  <KPI icon={Activity} label="Tokens entrée" value={fmtTokens(admin.global.input_tokens)} />
                  <KPI icon={Activity} label="Tokens sortie" value={fmtTokens(admin.global.output_tokens)} />
                  <KPI icon={TrendingDown} label="Coût global" value={`${admin.global.cost_eur.toFixed(3)} €`} />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Par modèle</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {admin.by_model.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Aucune donnée.</p>
                      ) : (
                        <div className="space-y-2">
                          {admin.by_model.map((m, i) => (
                            <div key={i} className="flex items-center justify-between py-1.5 border-b border-border/30 last:border-0">
                              <div>
                                <p className="text-sm font-medium">{m.model}</p>
                                <p className="text-xs text-muted-foreground">{m.provider} · {m.calls} appels</p>
                              </div>
                              <p className="font-semibold text-primary">{fmtCostEur(m.cost_usd)}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Users className="h-4 w-4" /> Top utilisateurs
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {admin.top_users.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Aucune donnée.</p>
                      ) : (
                        <div className="space-y-2">
                          {admin.top_users.map((u, i) => (
                            <div key={i} className="flex items-center justify-between py-1.5 border-b border-border/30 last:border-0">
                              <div>
                                <p className="text-sm font-mono">{u.user_id.slice(0, 16)}…</p>
                                <p className="text-xs text-muted-foreground">{u.calls} appels</p>
                              </div>
                              <p className="font-semibold">{fmtCostEur(u.cost_usd)}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}

const KPI = ({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) => (
  <div className="rounded-xl bg-card border border-border/50 p-4">
    <div className="flex items-center gap-2 mb-1">
      <Icon className="h-3.5 w-3.5 text-primary" />
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
    </div>
    <p className="text-xl font-bold text-foreground">{value}</p>
  </div>
);
